/**
 * Verbatim design kit.
 *
 * Every value here is lifted byte-for-byte from the Claude Design source, chosen by
 * frequency across all 83 screens (the shell appears 36 times, the muted label 70, and
 * so on). Screens compose these instead of restating inline styles, so the rendered DOM
 * is still the design's own but a screen's file is short enough to bind data into.
 *
 * Rule for this file: if a value is not in `design/screens/*.html`, it does not belong here.
 */

import type { CSSProperties, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useOptionalSession } from '../lib/session';
import { ReportIssue } from '../components/ReportIssue';
import { ShowMeAround } from '../components/ShowMeAround';

/* --------------------------------------------------------------- raw styles */

export const S = {
  /** Off-screen until focused — see the skip link in DesignShell. */
  skipLink: {
    position: 'absolute', left: '-9999px', top: '10px', zIndex: 100,
    background: '#fff', color: 'var(--c-ink)', fontSize: '0.8125rem', fontWeight: 600,
    padding: '12px 16px', borderRadius: '9px', textDecoration: 'none',
    border: '2px solid var(--c-brand)', minHeight: '44px', display: 'inline-flex', alignItems: 'center',
  } as CSSProperties,
  skipLinkFocused: { left: '10px' } as CSSProperties,
  // Layout
  screen: { display: 'flex', minHeight: '820px', background: 'var(--c-canvas)' } as CSSProperties,
  main: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' } as CSSProperties,
  /* The gap above the first control matches the gap between it and the content below. */
  body: { flex: 1, padding: '16px 30px 44px', display: 'flex', flexDirection: 'column', gap: '16px' } as CSSProperties,
  fill: { flex: 1, minWidth: 0 } as CSSProperties,

  // Header
  headerTitle: { fontSize: '1.5rem', fontWeight: 650, letterSpacing: '-.02em', lineHeight: 1.2, color: 'var(--c-ink)' } as CSSProperties,
  headerActions: { display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap', flex: 'none' } as CSSProperties,
  /** The context line under a title: curriculum, grade, and when it last moved. */
  headerMeta: { fontSize: '0.7188rem', color: 'var(--c-ink-muted)', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '7px' } as CSSProperties,

  // Surfaces
  card: { background: '#fff', border: '1px solid var(--c-line)', borderRadius: '13px', padding: '22px 24px' } as CSSProperties,
  cardFlush: { background: '#fff', border: '1px solid var(--c-line)', borderRadius: '13px', overflow: 'hidden' } as CSSProperties,
  panel: { background: '#fff', border: '1px solid var(--c-line)', borderRadius: '10px', padding: '14px 15px' } as CSSProperties,

  /*
   * Type scale.
   *
   * The planner screens had five sizes living between 0.6875rem and 0.9062rem, which is not
   * a hierarchy — it is five weights of the same size, and a teacher scanning an hour could
   * not tell a block title from the label above it. These six steps each have one job and a
   * real gap between them, so rank is legible at a glance rather than inferred from weight.
   *
   * `sectionLabel` below is deliberately NOT part of it: an all-caps tracked eyebrow over
   * every section is scaffolding, not hierarchy. Prefer `h2`/`h3` and keep the eyebrow for
   * the rare label that genuinely names a region rather than heading it.
   */
  h1: { fontSize: '1.375rem', fontWeight: 650, letterSpacing: '-.021em', lineHeight: 1.18 } as CSSProperties,
  h2: { fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-.012em', lineHeight: 1.28 } as CSSProperties,
  h3: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-.006em', lineHeight: 1.35 } as CSSProperties,
  prose: { fontSize: '0.8438rem', lineHeight: 1.55 } as CSSProperties,
  small: { fontSize: '0.7812rem', lineHeight: 1.5 } as CSSProperties,
  micro: { fontSize: '0.7188rem', lineHeight: 1.45 } as CSSProperties,

  // Type
  title: { fontSize: '0.9062rem', fontWeight: 600 } as CSSProperties,
  strong: { fontSize: '0.8125rem', fontWeight: 600 } as CSSProperties,
  muted: { color: 'var(--c-ink-muted)' } as CSSProperties,
  mutedSm: { fontSize: '0.75rem', color: 'var(--c-ink-muted)' } as CSSProperties,
  mutedXs: { fontSize: '0.7188rem', color: 'var(--c-ink-muted)' } as CSSProperties,
  sub: { fontSize: '0.75rem', color: 'var(--c-ink-muted)', marginTop: '3px' } as CSSProperties,
  link: { fontSize: '0.7812rem', fontWeight: 500, color: 'var(--c-brand)', cursor: 'pointer' } as CSSProperties,
  sectionLabel: {
    fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '.12em',
    textTransform: 'uppercase', color: 'var(--c-ink-muted)',
  } as CSSProperties,

  // Rows
  row: { display: 'flex', alignItems: 'center', gap: '10px' } as CSSProperties,
  rowGap11: { display: 'flex', alignItems: 'center', gap: '11px' } as CSSProperties,
  between: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' } as CSSProperties,

  // Table cells (the design uses spans in a grid, and real cells elsewhere)
  th: { padding: '12px 12px', fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--c-ink-muted)' } as CSSProperties,
  td: { padding: '13px 12px', fontSize: '0.7812rem', color: 'var(--c-ink-3)' } as CSSProperties,
} as const;

/* ------------------------------------------------------------------ buttons */

/*
 * Size and weight stay here; colour moves to `.kit-btn--*` in the stylesheet.
 *
 * An inline `background` beats any selector, so a button styled entirely inline can never
 * have a hover state — which is why the whole DesignShell layer had none. Splitting the two
 * gives every button in the app default, hover, focus, active and disabled without changing
 * how any of them look at rest.
 */
/*
 * Three real heights, not one.
 *
 * Every size used to carry `minHeight: 44px`, so "Sm" was the same button with smaller text
 * and a header of four controls read as a toolbar of slabs. 44px is the AAA touch target
 * (2.5.5); AA asks for 24px (2.5.8), which a 32px control clears with room to spare. Lg and
 * the default keep the generous target for the one action a screen is actually about; Sm and
 * Xs are for the dense authoring surfaces, where a row of chrome should not outweigh the
 * content it acts on.
 */
const BTN: Record<string, CSSProperties> = {
  primary: { fontSize: '0.8125rem', fontWeight: 600, padding: '0 15px', minHeight: '38px' },
  primaryLg: { fontSize: '0.8438rem', fontWeight: 600, padding: '0 20px', minHeight: '46px' },
  primarySm: { fontSize: '0.7656rem', fontWeight: 600, padding: '0 12px', minHeight: '32px' },
  secondary: { fontSize: '0.8125rem', fontWeight: 500, padding: '0 14px', minHeight: '38px' },
  secondaryLg: { fontSize: '0.8125rem', fontWeight: 500, padding: '0 18px', minHeight: '46px' },
  secondarySm: { fontSize: '0.7656rem', fontWeight: 500, padding: '0 12px', minHeight: '32px' },
  /** Icon-sized: a grip, a chevron, a nudge. Square, and never carrying a word. */
  ghostXs: { fontSize: '0.75rem', fontWeight: 500, padding: '0', minHeight: '28px', minWidth: '28px' },
  danger: { fontSize: '0.7656rem', fontWeight: 500, padding: '0 12px', minHeight: '32px' },
};

/** Which colour class each kind wears. */
const BTN_CLASS: Record<string, string> = {
  primary: 'kit-btn--primary',
  primaryLg: 'kit-btn--primary',
  primarySm: 'kit-btn--primary',
  secondary: 'kit-btn--secondary',
  secondaryLg: 'kit-btn--secondary',
  secondarySm: 'kit-btn--secondary',
  ghostXs: 'kit-btn--ghost',
  danger: 'kit-btn--danger',
};

export function Btn({
  kind = 'secondary',
  to,
  onClick,
  disabled,
  children,
  style,
  type = 'button',
  title,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  onKeyDown,
}: {
  kind?: keyof typeof BTN;
  to?: string;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
  style?: CSSProperties;
  type?: 'button' | 'submit';
  title?: string;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaControls?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
}) {
  const navigate = useNavigate();
  const css = { ...BTN[kind], ...style };
  return (
    <button
      type={type}
      title={title}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      onKeyDown={onKeyDown}
      className={`kit-btn ${BTN_CLASS[kind] ?? 'kit-btn--secondary'}`}
      style={css}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        if (onClick) onClick();
        else if (to) navigate(to);
      }}
    >
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------- chips */

const CHIP: Record<string, CSSProperties> = {
  good: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-good)', background: 'var(--c-good-wash)', padding: '5px 9px', borderRadius: '5px' },
  warn: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-warn-ink)', background: 'var(--c-warn-wash)', padding: '5px 9px', borderRadius: '5px' },
  brand: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-brand)', background: 'var(--c-brand-wash)', padding: '5px 9px', borderRadius: '5px' },
  bad: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-bad)', background: 'var(--c-bad-wash)', padding: '5px 9px', borderRadius: '5px' },
  plain: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-ink-3)', background: 'var(--c-surface-3)', padding: '5px 9px', borderRadius: '5px' },
  /* One tone per mastery band, read from the band tokens so chip and chart cannot disagree. */
  strong: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-band-strong-ink)', background: 'var(--c-band-strong-wash)', padding: '5px 9px', borderRadius: '5px' },
  secure: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-band-secure-ink)', background: 'var(--c-band-secure-wash)', padding: '5px 9px', borderRadius: '5px' },
  approaching: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-band-approaching-ink)', background: 'var(--c-band-approaching-wash)', padding: '5px 9px', borderRadius: '5px' },
  notYet: { fontSize: '0.6875rem', fontWeight: 600, color: 'var(--c-band-not-yet-ink)', background: 'var(--c-band-not-yet-wash)', padding: '5px 9px', borderRadius: '5px' },
};

/**
 * A chip is a label, not prose. Rows may wrap their titles; the chip beside them must not
 * break "part written" across two lines to make room.
 */
export function Chip({ tone = 'plain', children, style }: { tone?: keyof typeof CHIP; children: ReactNode; style?: CSSProperties }) {
  return <span style={{ ...CHIP[tone], whiteSpace: 'nowrap', flex: 'none', ...style }}>{children}</span>;
}

/** The mastery bands, in the design's own colours. Label always shown (screen 10i). */
export type Band = 'no_evidence' | 'not_yet' | 'approaching' | 'secure' | 'strong';

export const BAND_TONE: Record<Band, keyof typeof CHIP> = {
  no_evidence: 'plain',
  not_yet: 'notYet',
  approaching: 'approaching',
  secure: 'secure',
  strong: 'strong',
};

export const BAND_LABEL: Record<Band, string> = {
  no_evidence: 'No evidence',
  not_yet: 'Not yet',
  approaching: 'Approaching',
  secure: 'Secure',
  strong: 'Strong',
};

/** The one place a band becomes a colour; values live in tokens.css (`--c-band-*`). */
export const BAND_COLOR: Record<Band, string> = {
  no_evidence: 'var(--c-band-none)',
  not_yet: 'var(--c-band-not-yet)',
  approaching: 'var(--c-band-approaching)',
  secure: 'var(--c-band-secure)',
  strong: 'var(--c-band-strong)',
};

export function BandChip({ band, label }: { band: Band; label?: string }) {
  return <Chip tone={BAND_TONE[band]}>{label ?? BAND_LABEL[band]}</Chip>;
}

/** The stacked distribution bar the design uses on every performance screen. */
export function Spread({ spread, height = 8 }: { spread: Record<Band, number>; height?: number }) {
  const order: Band[] = ['not_yet', 'approaching', 'secure', 'strong'];
  const total = order.reduce((a, b) => a + (spread[b] ?? 0), 0) + (spread.no_evidence ?? 0);
  if (total === 0) return <span style={S.mutedXs}>No evidence yet</span>;
  const parts: Band[] = ['not_yet', 'approaching', 'secure', 'strong', 'no_evidence'];
  return (
    <div
      style={{ display: 'flex', height: `${height}px`, borderRadius: `${height / 2}px`, overflow: 'hidden', background: 'var(--c-track)', minWidth: '90px' }}
      role="img"
      aria-label={parts.map((b) => `${spread[b] ?? 0} ${BAND_LABEL[b]}`).join(', ')}
    >
      {parts.map((b) =>
        (spread[b] ?? 0) > 0 ? (
          <span key={b} style={{ width: `${((spread[b] ?? 0) / total) * 100}%`, background: BAND_COLOR[b] }} />
        ) : null,
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- shell */

export interface NavItem {
  label: string;
  to: string;
  /** The walkthrough's anchor for this entry, where it has one. See `lib/tour.ts`. */
  tour?: string;
  /** Inside a PYP class (B-1): the item's PYP name, or `null` to hide it for that class. */
  pyp?: string | null;
  /** Inside a PYP class: where the renamed item goes instead (the class rides along as `?cohort=`). */
  pypTo?: string;
  /** Hidden in a PYP class unless the school opted in (A.19b `allowAssessments`). */
  pypOptIn?: boolean;
  /** Student nav (§2.4): only for students in these programmes. */
  programmes?: ('pyp' | 'myp' | 'dp')[];
  /** Shown only once the server says so (§5.3): a sitting exists for one of your classes; your classes owe a project piece. */
  needs?: 'sittings' | 'projects' | 'campuses';
}

export interface NavGroup {
  /** The small-caps header. Absent for a role's top-level items, which never collapse. */
  label?: string;
  items: NavItem[];
}

export type NavRole = 'teacher' | 'admin' | 'owner' | 'student' | 'parent';

/** The group a screen-heavy role opens on (spec §2.3). */
const OPEN_BY_DEFAULT = 'Assessment & progress';

/*
 * The coordinator's groups (spec §5.3), shared with the owner, who passes every admin gate.
 * Items whose screens are not built yet are left out rather than linked to nothing; add
 * them to the group the spec names as each one lands.
 */
const COORDINATOR: NavGroup[] = [
  { items: [{ label: 'Overview', to: '/admin' }] },
  {
    label: 'Assessment & progress',
    items: [
      { label: 'Moderation', to: '/admin/moderation' },
      { label: 'Coverage', to: '/admin/coverage' },
      { label: 'Result analytics', to: '/admin/results' },
      /* Undrawn (§1.6): scheduling and access arrangements for B.22 / C.4a. */
      { label: 'E-Assessment sittings', to: '/admin/sittings' },
    ],
  },
  { label: 'Coursework', items: [{ label: 'Report comments', to: '/admin/comments' }, { label: 'Projects oversight', to: '/admin/projects' }] },
  {
    label: 'People',
    items: [
      { label: 'Teachers', to: '/admin/teachers' },
      { label: 'Students', to: '/admin/students' },
      /* Undrawn (spec §1.6); the classes the people above teach and sit in. */
      { label: 'Classes', to: '/admin/classes' },
      /* §5.3 moves this under Settings or Teachers as a tab; until those tabs exist it stays here. */
      { label: 'Attendance', to: '/admin/attendance' },
    ],
  },
  {
    label: 'School',
    items: [
      { label: 'Notices', to: '/admin/announcements' },
      { label: 'Evidence export', to: '/admin/exports' },
      { label: 'Practice activity', to: '/admin/practice' },
      { label: 'Settings', to: '/admin/settings' },
    ],
  },
];

/**
 * One nav per role, and only one.
 *
 * These used to be eight sets that each screen picked from by hand, which is how a teacher
 * ended up looking at the admin sidebar on their own event record, and how the same four
 * destinations changed name between Dashboard and Plans. The shell now reads the role and
 * chooses; screens do not get a say, so the nav cannot drift screen to screen.
 */
export const NAV: Record<NavRole, NavGroup[]> = {
  /*
   * `tour` is the anchor the walkthrough points at — a contract, not a styling hook, and
   * checked by `tour.test.ts`. Renaming one without updating `lib/tour.ts` fails the build.
   */
  teacher: [
    {
      items: [
        { label: 'Dashboard', to: '/teacher', tour: 'nav.dashboard' },
        /* The unit-plan flow is Units; the learning-events calendar and series scheduling sit under Live session. */
        { label: 'Units', to: '/teacher/unit-plans', tour: 'nav.unit-plans', pyp: 'Units of Inquiry', pypTo: '/teacher/pyp/units' },
        { label: 'Live session', to: '/teacher/events', tour: 'nav.events' },
      ],
    },
    {
      label: 'Assessment & progress',
      items: [
        { label: 'Assessments', to: '/teacher/assessments', tour: 'nav.assessments', pyp: null, pypOptIn: true },
        /* B.22, read only; appears once a sitting exists for one of the teacher's classes. */
        { label: 'E-Assessments', to: '/teacher/e-assessments', pyp: null, needs: 'sittings' },
        /* D-3: a PYP class has no bands; its evidence is curated into the portfolio (B.28). */
        { label: 'Mastery', to: '/teacher/mastery', pyp: 'Portfolio', pypTo: '/teacher/pyp/portfolio' },
        /* Teal, unsupervised (B.20); PYP has no simulator (§2.4). */
        { label: 'Home Practice', to: '/teacher/practice', pyp: null },
        /* Blind second marking a coordinator assigned (A.2); not in §5.3, but a marker needs a way in. */
        { label: 'Moderation', to: '/teacher/moderation', pyp: null },
      ],
    },
    {
      label: 'Coursework',
      /* B.25 individual long-form pieces; B.18 hangs off it. PYP: the Grade 5 Exhibition. */
      items: [{ label: 'Projects', to: '/teacher/projects', pyp: 'Exhibition' }],
    },
    {
      label: 'People',
      items: [
        { label: 'Students', to: '/teacher/students', tour: 'nav.students' },
        /* B.13 participation; the register is one click from it. */
        { label: 'Attendance', to: '/teacher/attendance' },
      ],
    },
    {
      label: 'School',
      items: [
        { label: 'Notices', to: '/teacher/notices' },
        { label: 'Library', to: '/teacher/unit-plans/library' },
      ],
    },
    /* Gradebook and term reports live under Analytics & Reports. */
    {
      items: [
        { label: 'Analytics & Reports', to: '/teacher/reports', pyp: 'Narrative reports', pypTo: '/teacher/pyp/reports' },
        /* A.11 from the teacher's side: the same page the coordinator sees. No bands in PYP (D-3). */
        { label: 'Growth insights', to: '/teacher/growth', pyp: null },
        { label: 'Settings', to: '/teacher/settings' },
      ],
    },
  ],
  admin: COORDINATOR,
  /*
   * §5.3 / §2.2 (D.1–D.4): the owner's four screens (Campuses hidden with one campus), then a door
   * into the coordinator's view. The coordinator's groups join only once the owner is inside it
   * (`navFor` ctx.coordinatorView).
   */
  owner: [
    {
      items: [
        { label: 'Executive overview', to: '/admin/executive' },
        { label: 'Campuses', to: '/admin/rollup', needs: 'campuses' },
        { label: 'Enrolment stats', to: '/admin/enrolment' },
        { label: 'Billing', to: '/admin/billing' },
      ],
    },
    { items: [{ label: 'Coordinator view', to: '/admin' }] },
  ],
  parent: [{ items: [{ label: 'Your children', to: '/parent' }, { label: 'Notices', to: '/notices' }] }],
  /*
   * §2.4 / §5.3: PYP students get Home, My classes and Portfolio (Projects for G5 lands in
   * tranche 5); MYP has no CAS; CAS stays under Coursework for DP students only.
   */
  student: [
    /* PYP: Portfolio is the student's main surface, so it sits at the top, not inside Coursework. */
    { items: [{ label: 'Home', to: '/student' }, { label: 'My classes', to: '/student/classes' }, { label: 'Portfolio', to: '/student/portfolio', programmes: ['pyp'] }] },
    {
      label: 'Assessment & progress',
      items: [
        { label: 'Assessments', to: '/student/results', programmes: ['myp', 'dp'] },
        { label: 'E-Assessment', to: '/student/e-assessment', programmes: ['myp', 'dp'], needs: 'sittings' },
        { label: 'Practice', to: '/student/practice', programmes: ['myp', 'dp'] },
      ],
    },
    {
      label: 'Coursework',
      items: [
        /* C-5: the server says whether this student's classes owe a piece (all DP, MYP5, PYP Grade 5). */
        { label: 'Projects', to: '/student/projects', needs: 'projects' },
        /* B.15: the student's side of a group task — their group's report and their own reflection. */
        { label: 'Group tasks', to: '/student/group-tasks', programmes: ['myp', 'dp'] },
        { label: 'CAS', to: '/student/cas', programmes: ['dp'] },
      ],
    },
    {
      items: [
        { label: 'Progress', to: '/student/performance', programmes: ['myp', 'dp'] },
        { label: 'Notices', to: '/notices' },
        { label: 'What’s new', to: '/student/whats-new' },
        { label: 'Account', to: '/student/account' },
      ],
    },
  ],
} satisfies Record<string, NavGroup[]>;

/**
 * The role's groups, with the PYP class swap applied when the screen is inside a PYP class,
 * the student's programme applied (§2.4; an unknown programme reads as MYP: no CAS, no
 * Portfolio), and conditional items dropped until the server says they apply.
 */
export function navFor(
  role: NavRole | undefined,
  curriculumKey?: string,
  ctx: { programme?: string; sittings?: boolean; projects?: boolean; campuses?: boolean; pypAssessments?: boolean; cohortId?: string; coordinatorView?: boolean } = {},
): NavGroup[] {
  const programme = ctx.programme === 'pyp' || ctx.programme === 'dp' ? ctx.programme : 'myp';
  const base = role === 'owner' && ctx.coordinatorView ? [...NAV.owner, ...COORDINATOR.slice(1)] : (NAV[role ?? 'student'] ?? NAV.student);
  const groups = base
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => (!i.needs || ctx[i.needs]) && (role !== 'student' || !i.programmes || i.programmes.includes(programme))),
    }))
    .filter((g) => g.items.length > 0);
  if (curriculumKey !== 'ib_pyp') return groups;
  return groups
    .map((g) => ({
      ...g,
      items: g.items.flatMap((i) => {
        if (i.pyp === null) return i.pypOptIn && ctx.pypAssessments ? [i] : [];
        if (!i.pyp) return [i];
        const to = i.pypTo ? `${i.pypTo}${ctx.cohortId ? `?cohort=${encodeURIComponent(ctx.cohortId)}` : ''}` : i.to;
        return [{ ...i, label: i.pyp, to }];
      }),
    }))
    .filter((g) => g.items.length > 0);
}

const OWNER_SCREENS = ['/admin/executive', '/admin/rollup', '/admin/enrolment', '/admin/billing'];
/** An owner on any admin screen other than their own four is in the coordinator's view. */
const inCoordinatorView = (pathname: string) => pathname.startsWith('/admin') && !OWNER_SCREENS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Which nav item the current URL sits under.
 *
 * Derived from the path rather than passed in, because a screen naming its own highlight is
 * a second place for the truth to live — and it drifted: the timetable used to light up
 * “Dashboard”. A one-segment root (`/teacher`) only matches exactly, so a deeper screen
 * highlights nothing rather than falsely lighting the home entry.
 */
function activeItem(nav: NavItem[], pathname: string): NavItem | null {
  let best: NavItem | null = null;
  for (const item of nav) {
    const to = item.to.split('?')[0]!;
    const segments = to.split('/').filter(Boolean).length;
    const matches = pathname === to || (segments > 1 && pathname.startsWith(`${to}/`));
    if (matches && (!best || to.length > best.to.split('?')[0]!.length)) best = item;
  }
  return best;
}

/**
 * B-1: which class the screen is in, and whether it is a PYP class. The screen's own prop wins;
 * then `?cohort=` or a cohort id in the path; then the PYP screens themselves. With no class in
 * context, a teacher whose classes are all PYP gets PYP mode everywhere — one shell, no second app.
 */
export function useClassContext(explicit?: string): { curriculumKey?: string; cohortId?: string } {
  const session = useOptionalSession();
  const { pathname, search } = useLocation();
  const user = session?.user;
  if (!user || user.role === 'student' || user.role === 'parent') return { curriculumKey: explicit };
  const cohorts = session?.cohorts ?? [];
  const keyOf = (c: (typeof cohorts)[number]) => c.curriculumKey ?? (c.programme === 'pyp' ? 'ib_pyp' : undefined);
  const segments = pathname.split('/');
  const queried = new URLSearchParams(search).get('cohort');
  const inContext = cohorts.find((c) => c.id === queried) ?? cohorts.find((c) => segments.includes(c.id));
  const cohortId = inContext?.id ?? queried ?? undefined;
  if (explicit) return { curriculumKey: explicit, cohortId };
  if (inContext) return { curriculumKey: keyOf(inContext), cohortId };
  if (pathname.startsWith('/teacher/pyp')) return { curriculumKey: 'ib_pyp', cohortId };
  if (user.role === 'teacher') {
    const mine = cohorts.filter((c) => c.teacherIds.includes(user.id));
    if (mine.length && mine.every((c) => keyOf(c) === 'ib_pyp')) return { curriculumKey: 'ib_pyp' };
  }
  return {};
}

/*
 * Which groups the reader has opened or closed. Module-level so it survives the shell
 * remounting on every screen; resets on reload, which is when the defaults are right again.
 */
const navOpen: Record<string, boolean> = {};

const NavGlyph = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false" style={{ flex: 'none', opacity: 0.7 }}>
    <rect x="0.5" y="0.5" width="4.5" height="4.5" rx="1" fill="none" stroke="currentColor" />
    <rect x="7" y="0.5" width="4.5" height="4.5" rx="1" fill="none" stroke="currentColor" />
    <rect x="0.5" y="7" width="4.5" height="4.5" rx="1" fill="none" stroke="currentColor" />
    <rect x="7" y="7" width="4.5" height="4.5" rx="1" fill="none" stroke="currentColor" />
  </svg>
);

/**
 * The grouped sidebar nav, used by both shells. Groups collapse; the progress group and
 * whichever group holds the current screen start open, so the tour's anchors and the
 * reader's own place are never folded away.
 */
export function RoleNav({ curriculumKey }: { curriculumKey?: string }) {
  const session = useOptionalSession();
  const { pathname } = useLocation();
  const cls = useClassContext(curriculumKey);
  const groups = navFor(session?.user?.role, cls.curriculumKey, {
    programme: session?.user?.programmes?.[0], sittings: session?.nav?.sittings, projects: session?.nav?.projects, campuses: session?.nav?.campuses,
    pypAssessments: session?.nav?.pypAssessments, cohortId: cls.cohortId, coordinatorView: inCoordinatorView(pathname),
  });
  const active = activeItem(groups.flatMap((g) => g.items), pathname);
  const [, rerender] = React.useState(0);
  /* No one signed in yet (the session is still loading): no nav, rather than a student's or the wrong mode's flashing first. */
  if (session && !session.user) return <nav className="kit-nav" aria-label="Main" />;

  return (
    <nav className="kit-nav" aria-label="Main">
      {groups.map((g, gi) => {
        const holdsActive = !!active && g.items.includes(active);
        const open = !g.label || holdsActive || (navOpen[g.label] ?? g.label === OPEN_BY_DEFAULT);
        const id = `nav-group-${gi}`;
        return (
          <div key={g.label ?? `top-${gi}`} className="kit-nav__group">
            {g.label ? (
              <button
                type="button"
                className="kit-nav__head"
                aria-expanded={open}
                aria-controls={id}
                disabled={holdsActive}
                onClick={() => {
                  navOpen[g.label!] = !open;
                  rerender((n) => n + 1);
                }}
              >
                <span>{g.label}</span>
                {!holdsActive && <span aria-hidden="true" className="kit-nav__chev">{open ? '−' : '+'}</span>}
              </button>
            ) : null}
            <div id={id} className="kit-nav__items" hidden={!open}>
              {g.items.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-tour={item.tour}
                  aria-current={item === active ? 'page' : undefined}
                  className="kit-nav__link"
                >
                  <NavGlyph />
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

const ROLE_TITLE: Record<NavRole, string> = {
  owner: 'Owner',
  admin: 'Coordinator',
  teacher: 'Teacher',
  student: 'Student',
  parent: 'Parent',
};

/** "Northgate International · 486 students": who this screen is about, from what the session already holds. */
function useContextLine(): string | null {
  const session = useOptionalSession();
  if (!session?.school) return null;
  const parts = [session.school.name];
  const role = session.user?.role;
  if (role === 'admin' || role === 'owner') {
    const students = new Set(session.cohorts.flatMap((c) => c.studentIds ?? [])).size;
    if (students > 0) parts.push(`${students.toLocaleString()} students`);
  }
  return parts.join(' · ');
}

/**
 * The design's app shell: a 212px navy aside with grouped nav and the identity block, a slim
 * breadcrumb strip with the school on the right, then the title on the cream canvas.
 *
 * The nav and the highlight are derived here, from the signed-in role and the current URL.
 * Screens used to pass both, and both went wrong — an event record built for admins showed
 * teachers a sidebar of links they are not allowed to open, which bounced them back to their
 * dashboard. There is no prop to get that wrong with any more.
 */
export interface Crumb {
  label: string;
  to?: string;
}

export function DesignShell({
  brand,
  title,
  actions,
  children,
  bodyStyle,
  footer,
  crumbs,
  meta,
  curriculumKey,
}: {
  brand?: string;
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  bodyStyle?: CSSProperties;
  /** Extra line under the name — the student shell carries their cohort here (1i). */
  footer?: { name: string; sub?: string };
  /**
   * The trail back out. A teacher three levels deep (unit plans → a unit → one hour of it)
   * had one "Back to the unit" button and no way to tell where they were; the sidebar only
   * highlights the section. The last crumb is the current page and never a link.
   */
  crumbs?: Crumb[];
  /** Curriculum, grade, and when this last moved. Sits under the title, not in it. */
  meta?: ReactNode;
  /** The class this screen is scoped to; `ib_pyp` swaps the class-scoped nav items (B-1). */
  curriculumKey?: string;
}) {
  const session = useOptionalSession();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = session?.user ?? null;
  const name = footer?.name || user?.name || '';
  /* "Vice Principal", "DP Coordinator" (A-15): the job title where one is set, else the role. */
  const roleTitle = user?.title || ROLE_TITLE[user?.role ?? 'student'];
  const context = useContextLine();
  /* With no trail given, the strip names the section the sidebar highlights. */
  const cls = useClassContext(curriculumKey);
  const here = activeItem(navFor(user?.role, cls.curriculumKey, { cohortId: cls.cohortId, coordinatorView: inCoordinatorView(pathname) }).flatMap((g) => g.items), pathname);
  const trail: Crumb[] = crumbs && crumbs.length ? crumbs : here ? [{ label: here.label }] : [];
  const initials = user?.avatarInitials || name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="kit-shell">
      {/*
        Every screen puts the same nav links before its content. Without this a keyboard or
        screen-reader user tabs through all of them on every single screen (WCAG 2.4.1, A).
        Visually hidden until focused, then it appears over the sidebar.
      */}
      <a href="#main" style={S.skipLink} onFocus={(e) => Object.assign(e.currentTarget.style, S.skipLinkFocused)} onBlur={(e) => Object.assign(e.currentTarget.style, S.skipLink)}>
        Skip to content
      </a>
      <aside className="kit-aside">
        <div className="kit-brand">
          <div className="kit-brand__name">{brand ?? 'SchoolVerse'}</div>
          {user ? <div className="kit-brand__role">{roleTitle}</div> : null}
        </div>
        <RoleNav curriculumKey={curriculumKey} />
        {/*
          The foot of the sidebar: the two things you can always do, and who you are.
          Signing out is the one control that has to be on every screen: a shared machine
          in a staffroom is the normal case, and this shell once had no way off it at all.
        */}
        <div className="kit-foot">
          <ShowMeAround />
          <ReportIssue />
          {(user || name) && (
          <div className="kit-who">
            <span className="kit-who__avatar" aria-hidden="true">{initials}</span>
            <div className="kit-who__id">
              <div className="kit-who__name" title={name}>{name}</div>
              <div className="kit-who__sub" title={footer?.sub ?? roleTitle}>{footer?.sub ?? roleTitle}</div>
            </div>
            {session ? (
              <button
                type="button"
                onClick={() => {
                  session.signOut();
                  navigate('/signin');
                }}
                className="kit-signout"
                aria-label="Sign out"
                title="Sign out"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <path d="m16 17 5-5-5-5" />
                  <path d="M21 12H9" />
                </svg>
              </button>
            ) : null}
            </div>
          )}
        </div>
      </aside>

      <main id="main" tabIndex={-1} className="kit-main">
        <header className="kit-strip">
          {trail.length ? (
            <nav aria-label="Breadcrumb" className="crumbs">
              <ol>
                {trail.map((c, i) => {
                  const last = i === trail.length - 1;
                  return (
                    <li key={`${c.label}-${i}`}>
                      {c.to && !last ? (
                        <Link to={c.to}>{c.label}</Link>
                      ) : (
                        <span aria-current={last ? 'page' : undefined}>{c.label}</span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
          ) : <span />}
          {context ? <div className="kit-context">{context}</div> : null}
        </header>
        {session?.school?.sample && (
          /* Seeded history looks like a real term; this is what stops it being mistaken for one. */
          <div role="note" style={{ background: 'var(--c-warn-wash)', color: 'var(--c-warn-ink)', border: '1px solid var(--c-line)', borderRadius: '9px', padding: '9px 14px', fontSize: '0.8125rem', lineHeight: 1.5, margin: '14px 30px 0' }}>
            <strong style={{ fontWeight: 600 }}>Sample data.</strong> This school is seeded for demonstration. No student here is real.
          </div>
        )}
        {/*
          Centred, not top-aligned. The row's height is set by whichever action button is
          tallest, so pinning the title to flex-start left it sitting high in the strip.
        */}
        <div className="kit-titlebar">
          <div style={{ minWidth: 0 }}>
            {/* The one h1 per screen. Body headings sit below it as h2. */}
            <h1 style={{ ...S.headerTitle, margin: 0 }}>{title}</h1>
            {meta ? <div style={{ ...S.headerMeta, marginTop: '4px' }}>{meta}</div> : null}
          </div>
          {actions ? <div style={S.headerActions}>{actions}</div> : null}
        </div>
        <div className="kit-body" style={{ ...S.body, ...bodyStyle }}>{children}</div>
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------- states */

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div style={{ padding: '60px 0', textAlign: 'center', ...S.mutedSm }} aria-live="polite">
      {label}…
    </div>
  );
}

/**
 * A skeleton in the shape of what is coming.
 *
 * A centred "Loading…" tells the reader nothing about where to look next and makes every
 * screen feel like it starts from nothing. These hold the layout, so the page arrives rather
 * than appears — which also stops the content jumping when it lands.
 *
 * `variant` names the screen's real shape; anything else falls back to a plain list.
 */
export function Skeleton({
  variant = 'list',
  rows = 6,
}: {
  variant?: 'list' | 'filtered-list' | 'calendar' | 'detail' | 'cards';
  rows?: number;
}) {
  const bar = (w: string, h = 12) => (
    <span className="sk" style={{ width: w, height: `${h}px` }} aria-hidden="true" />
  );

  if (variant === 'calendar') {
    return (
      <div className="sk-wrap" role="status" aria-label="Loading">
        <div className="sk-row">{bar('180px', 22)}{bar('90px')}</div>
        <div className="sk-cal">
          {Array.from({ length: 7 }, (_, i) => (
            <div key={i} className="sk-calcell">
              {bar('60%', 10)}
              {i % 3 !== 2 ? <span className="sk sk--chip" aria-hidden="true" /> : null}
              {i % 2 === 0 ? <span className="sk sk--chip" aria-hidden="true" /> : null}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'detail') {
    return (
      <div className="sk-wrap" role="status" aria-label="Loading">
        <div className="sk-card">{bar('45%', 16)}{bar('85%')}{bar('70%')}</div>
        <div className="sk-two">
          <div className="sk-card">{bar('40%', 14)}{bar('90%')}{bar('80%')}{bar('60%')}</div>
          <div className="sk-card">{bar('50%', 14)}{bar('75%')}{bar('85%')}</div>
        </div>
      </div>
    );
  }

  if (variant === 'cards') {
    return (
      <div className="sk-wrap" role="status" aria-label="Loading">
        <div className="sk-cards">
          {Array.from({ length: rows }, (_, i) => (
            <div key={i} className="sk-card">{bar('70%', 14)}{bar('45%')}{bar('55%')}</div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="sk-wrap" role="status" aria-label="Loading">
      {variant === 'filtered-list' ? (
        <div className="sk-row">{bar('190px', 34)}{bar('90px', 34)}{bar('90px', 34)}{bar('120px', 34)}</div>
      ) : null}
      <div className="sk-card sk-card--flush">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="sk-line">
            <span style={{ display: 'grid', gap: '6px', flex: 1 }}>
              {bar(`${55 + ((i * 13) % 30)}%`, 13)}
              {bar(`${30 + ((i * 7) % 25)}%`, 10)}
            </span>
            <span className="sk sk--chip" aria-hidden="true" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ErrorNote({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div
      role="alert"
      style={{ background: 'var(--c-bad-wash)', color: 'var(--c-bad)', borderRadius: '10px', padding: '13px 15px', fontSize: '0.7812rem', display: 'flex', alignItems: 'center', gap: '12px' }}
    >
      <span>{message}</span>
      {retry ? <Btn kind="secondarySm" onClick={retry}>Try again</Btn> : null}
    </div>
  );
}

export function Empty({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div style={{ padding: '54px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
      <div style={{ fontSize: '0.9062rem', fontWeight: 600 }}>{title}</div>
      {body ? <div style={{ ...S.mutedSm, maxWidth: '420px', lineHeight: 1.6 }}>{body}</div> : null}
      {action}
    </div>
  );
}

/** The design's table shape: a card with a header row and hairline-separated rows. */
export function Table({
  columns,
  children,
  minWidth,
}: {
  columns: { label: string; width?: string; align?: 'left' | 'right' }[];
  children: ReactNode;
  minWidth?: number;
}) {
  /* A wide table scrolls sideways; a keyboard user must be able to focus it to scroll (WCAG 2.1.1, tranche 8 axe pass). */
  return (
    <div role="region" tabIndex={0} aria-label={`Table: ${columns.map((c) => c.label).filter(Boolean).slice(0, 3).join(', ')}`} style={{ ...S.cardFlush, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: minWidth ? `${minWidth}px` : undefined }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.label}
                scope="col"
                style={{ ...S.th, width: c.width, textAlign: c.align ?? 'left', borderBottom: '1px solid var(--c-line)', whiteSpace: 'nowrap' }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Td({ children, style, align }: { children?: ReactNode; style?: CSSProperties; align?: 'left' | 'right' }) {
  return (
    <td style={{ ...S.td, borderTop: '1px solid var(--c-line-soft)', textAlign: align ?? 'left', ...style }}>{children}</td>
  );
}

/* ------------------------------------------------------------- filter bar */

export interface FilterOption {
  key: string;
  label: string;
  n: number;
}

/**
 * Search plus counted filter pills — the shape the roster already uses, lifted here so the
 * two lists behave the same way rather than each inventing one.
 *
 * The counts are the point: a filter that might be empty should say so before it is clicked,
 * so nobody hunts through four tabs looking for a unit that is in the first one.
 */
export function FilterBar({
  q,
  onQ,
  placeholder,
  filters,
  active,
  onFilter,
  children,
}: {
  q: string;
  onQ: (v: string) => void;
  placeholder: string;
  filters: FilterOption[];
  active: string;
  onFilter: (key: string) => void;
  /** Extra controls — a grade select, a sort. Sits after the pills. */
  children?: ReactNode;
}) {
  return (
    <div className="filterbar">
      <label className="filterbar__search">
        <span aria-hidden="true">⌕</span>
        <input value={q} onChange={(e) => onQ(e.target.value)} placeholder={placeholder} aria-label={placeholder} />
        {q ? (
          <button type="button" aria-label="Clear search" onClick={() => onQ('')} className="filterbar__clear">
            ✕
          </button>
        ) : null}
      </label>

      <div className="filterbar__pills" role="group" aria-label="Filter">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            aria-pressed={active === f.key}
            onClick={() => onFilter(f.key)}
            className="filterbar__pill"
            disabled={f.n === 0 && f.key !== 'all' && active !== f.key}
          >
            {f.label} <span className="filterbar__n">{f.n}</span>
          </button>
        ))}
      </div>

      {children}
    </div>
  );
}

/* -------------------------------------------------------------------- modal */

/**
 * A centred dialog for a choice that deserves the whole screen's attention.
 *
 * Picking a different activity used to unfold inline underneath the block, which pushed the
 * rest of the hour down the page and gave fourteen options a strip two rows tall. A choice
 * between fourteen things is a choice worth stopping for.
 *
 * Escape closes, the backdrop closes, focus moves in on open and returns to whatever opened
 * it on close, and a Tab cycle is trapped inside — otherwise the page behind stays reachable
 * to a keyboard while it is visually unreachable.
 */
export function Modal({
  title,
  blurb,
  onClose,
  children,
  footer,
  width = 640,
}: {
  title: string;
  blurb?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    returnTo.current = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(
        panel.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href], input:not(:disabled), select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      );
    focusables()[0]?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      returnTo.current?.focus?.();
    };
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="modal-panel"
        style={{ width: `min(${width}px, 100%)` }}
      >
        <div className="modal-head">
          <div style={{ minWidth: 0 }}>
            <h2 style={{ ...S.h2, margin: 0 }}>{title}</h2>
            {blurb ? <p style={{ ...S.small, color: 'var(--c-ink-muted)', margin: '4px 0 0' }}>{blurb}</p> : null}
          </div>
          <Btn kind="ghostXs" ariaLabel="Close" onClick={onClose}>
            ✕
          </Btn>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/** "just now" / "2 hours ago" / "12 March" — enough to know whether it is stale. */
export function formatSince(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days <= 6) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
}

export function formatMinutes(total: number): string {
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

export function formatDay(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}
